import { Box, Flex, Text as ChakraText, Center, Spinner } from '@chakra-ui/react'
import { createBrowserHistory } from 'history'
import { ReactElement, Suspense, useMemo, useRef, lazy } from 'react'
import { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { ListItem, UnorderedList } from '@chakra-ui/layout'
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom'
import { CachePolicies, Provider as FetchProvider } from 'use-http'
import ElevationProvider from './providers/ElevationProvider'
import { isEqual, isNumber } from 'lodash-es'
import { GlobalStyle } from '../styles/global-styles'
import { checkPermission, PermissionsEnum } from '../utils/permissions'
import PermissionGuard from './components/PermissionGuard'
import SessionCheck from './components/SessionCheck'
import { FullPageLoadingSpinner, NavBarSkeleton } from './components/LoadingSpinner/index'

// Lazy load all route components to reduce initial bundle size
const IDRegion = lazy(() => import('./components/GetStarted').then(m => ({ default: m.IDRegion })))

// Delaying NavBar load slightly to prioritize loading of main content chunks and let necessary data loading API calls to start sooner.
const NavBarLayout = lazy(() => new Promise(resolve => setTimeout(resolve, 50)).then(() => import('./layouts/NavBar/NavBarLayout')))
const HomePage = lazy(() => import('./pages/Home/HomePage'))
const ExternalStorageRouter = lazy(() => import('./pages/ExternalStorage').then(m => ({ default: m.ExternalStorageRouter })))
const PathPolicyRouter = lazy(() => import('./pages/PathPolicy').then(m => ({ default: m.PathPolicyRouter })))
const CreativeAutomationRouter = lazy(() => import('./pages/CreativeAutomation').then(m => ({ default: m.CreativeAutomationRouter })))
const UrlEndpointsRouter = lazy(() => import('./pages/UrlEndpoints').then(m => ({ default: m.UrlEndpointsRouter })))
const PerformanceRouter = lazy(() => import('./pages/Performance').then(m => ({ default: m.PerformanceRouter })))
const SettingsRouter = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsRouter })))
const DeveloperPage = lazy(() => import('./pages/Developer/index'))
const PurgeCachePage = lazy(() => import('./pages/PurgeCache'))
const UserProfilePage = lazy(() => import('./pages/Profile'))
const UserManagementRouter = lazy(() => import('./pages/Users').then(m => ({ default: m.UserManagementRouter })))
const BillingRouter = lazy(() => import('./pages/Billing').then(m => ({ default: m.BillingRouter })))
const AWSManagedPage = lazy(() => import('./pages/Billing').then(m => ({ default: m.AWSManagedPage })))
const PlanChangePage = lazy(() => import('./pages/Billing').then(m => ({ default: m.PlanChangePage })))
const InvoiceRouter = lazy(() => import('./pages/Billing').then(m => ({ default: m.InvoiceRouter })))
const NotFoundPage = lazy(() => import('./pages/NotFound/NotFoundPage'))
const NotificationPage = lazy(() => import('./pages/Notification/NotificationPage'))
const AnalyticsRouter = lazy(() => import('./pages/UsageAnalytics').then(m => ({ default: m.AnalyticsRouter })))
const MediaLibraryRouter = lazy(() => import('./pages/MediaLibrary/Router'))
const SupportRouter = lazy(() => import('./pages/Support').then(m => ({ default: m.SupportRouter })))
const ReferralsPage = lazy(() => import('./pages/Referrals'))
const AuditLogsPage = lazy(() => import('./pages/AuditLogs'))
const MediaAuditLogsPage = lazy(() => import('./pages/MediaAuditLogs'))
const LabsRouter = lazy(() => import('./pages/Labs/Router').then(m => ({ default: m.LabsRouter })))
const AgencyAnalyticsPage = lazy(() => import('./pages/Agency/AgencyAnalyticsPage'))
const AgencyManageAccountsRouter = lazy(() => import('./pages/Agency/Router'))
const ChatReviewListPage = lazy(() => import('./pages/ChatReview/ChatReviewListPage'))
const ChatReviewDetailPage = lazy(() => import('./pages/ChatReview/ChatReviewDetailPage'))
const VideoAnalyticsRouter = lazy(() => import('./pages/VideoAnalytics/VideoAnalyticsRouter'))
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsent'))

import ErrorPage from './pages/Error/ErrorPage'
import { LazyErrorBoundary } from './components/LazyErrorBoundary'
import { initSentry, withLazySentryProfiler } from '../utils/sentry'
import { CSRFContext } from './providers/CSRFProvider'
import { UserContext } from './providers/UserProvider'
import { RouterBackStackProvider } from './providers/RouterBackStackProvider'
import { CustomMetadataProvider } from './providers/CustomMetadataProvider'
import { PublicLinksInfoContext } from './providers/PublicLinksInfoProvider'
import { useCookies } from 'react-cookie'
import { DiscountedPricingClosableInfoBar } from './components/Billing/DiscountedPricing'
import { useMLWidgetContext } from './contexts/MLWidgetContext'
import type { MLWidgetSettings } from './interfaces'

import './contextMenu.css'

const HideOnOAuth = ({ children }: { children: React.ReactNode }): React.ReactElement | null => {
  const { pathname } = useLocation()
  if (pathname === '/oauth') return null
  return <>{children}</>
}

const IKDashboardApp = (): ReactElement | null => {
  const { i18n } = useTranslation()
  const { token, loading } = useContext(CSRFContext)
  const { user, loading: userLoading } = useContext(UserContext)
  const [cookies, setCookie] = useCookies(['validateLoginRequestedFromApp'])

  const { isMediaLibraryWidget = false } = window
  const {
    isPublicLink,
    assetType,
    assetId,
    assetBase64Path,
    urlPrefix,
  } = useContext(PublicLinksInfoContext)

  const routingBaseName = (
    isMediaLibraryWidget ?
      '/media-library-widget/' : (
        isPublicLink ?
          urlPrefix :
          '/dashboard/'
      )
  )

  const prevBaseRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevBaseRef.current !== routingBaseName) {
      prevBaseRef.current = routingBaseName
    }
  }, [routingBaseName])

  const history = createBrowserHistory({
    basename: routingBaseName
  })

  const { updateSettings } = useMLWidgetContext()

  useEffect(() => {
    // Lazily initialize Sentry after initial render to reduce bundle size
    void initSentry(history)
    // window.Headway.init(window.HeadwayConfig)

    const mlWidgetMessageListener = (event: MessageEvent): void => {
      try {
        const newSettings: MLWidgetSettings = {}

        if (window.widgetHost) {
          const widgetHostOrigin = new URL(window.widgetHost).origin

          if (
            !isEqual(event.origin.toString(), widgetHostOrigin.toString())
          ) {
            return
          }

          const data = JSON.parse(event.data)

          if (data) {
            if (data?.mlSettings?.multiple === false) {
              newSettings.multiple = false
            } else {
              newSettings.multiple = true
              if (
                data?.mlSettings?.maxFiles &&
                isNumber(data.mlSettings.maxFiles) &&
                data.mlSettings.maxFiles > 0
              ) {
                newSettings.maxFiles = data.mlSettings.maxFiles
              }
            }

            if (data?.mlSettings?.initialView) {
              newSettings.initialView = data.mlSettings.initialView
            }

            if (data?.mlSettings?.toolbar) {
              newSettings.toolbar = data.mlSettings.toolbar
            }

            updateSettings(newSettings)
          }
        }
      } catch (e) {
        console.error('Error occurred in MediaLibraryWidget postMessage', e)
      }
    }

    window.addEventListener('message', mlWidgetMessageListener, false)

    return () => {
      window.removeEventListener('message', mlWidgetMessageListener)
    }
  }, [])

  const exceededArray = useMemo(() => {
    if (isPublicLink) {
      return []
    }

    const exceededArray: string[] = [];

    if (!user?.accountEnabled) exceededArray.push("The account is disabled. Please get in touch with support at support@imagekit.io.");
    if (user?.bandwidthExceeded) exceededArray.push("Bandwidth usage exceeded free plan limits.");
    if (user?.storageExceeded) exceededArray.push("Storage usage exceeded free plan limits.");
    if (user?.limitExceeded?.extensions) {
      if (user?.pricingPlan?.isPaidPlan) {
        if (user?.extensionMaximumOverage && user?.extensionMaximumOverage > 0) {
          exceededArray.push(`Extensions usage exceeded plan limits. Maximum ${user?.extensionMaximumOverage} extensions units can be consumed in a month in the current plan.`);
        } else {
          exceededArray.push("Extensions usage exceeded plan limits.");
        }
      } else {
        exceededArray.push("Extensions usage exceeded free plan limits.");
      }
    }
    if (user?.limitExceeded?.transformations?.video) exceededArray.push("Video transformation usage exceeded free plan limits.");

    return exceededArray
  }, [user, isPublicLink])

  // if found URL is "/", then redirect to this URL
  const routeSuffixToRedirectToForBaseUrl = useMemo<string>(() => {
    let routeSuffix = "/media-library"

    if (isPublicLink) {
      if (
        assetType === "file" &&
        assetId
      ) {
        routeSuffix = `/media-library/detail/${assetId}`
      } else if (
        assetType === "folder" &&
        assetBase64Path
      ) {
        routeSuffix = `/media-library/${assetBase64Path}`
      } else if (
        assetType === "media-collection" &&
        assetId
      ) {
        routeSuffix = `/media-collections/${assetId}`
      }
    }

    return routeSuffix
  }, [
    isPublicLink,
  ])

  if (
    !isPublicLink &&
    (
      userLoading ||
      !(user && user.clientNumber && user.imagekitId && user.userEmail)
    )
  ) {
    return (
      <Center width="full" height="100vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }

  if (loading) {
    return null
  }

  return (
    !cookies.validateLoginRequestedFromApp ?
      <LazyErrorBoundary fallback={ErrorPage} showDialog>
        <FetchProvider
          options={{
            cacheLife: 60000,
            cachePolicy: CachePolicies.NO_CACHE,
            interceptors: {
              request: ({ options, url }) => {
                if (options.headers) {
                  options.headers['Content-Type'] = 'application/json'
                } else {
                  options.headers = { 'Content-Type': 'application/json' }
                }

                if (
                  url &&
                  window.location.host === new URL(url).host &&
                  options.method &&
                  !['GET', 'HEAD', 'OPTIONS'].includes(options.method)
                ) {
                  options.headers['csrf-token'] = token
                }

                return options
              },
              response: async ({ response }) => {
                // it would mean that the public link password has changed, so make the user enter it again by reloading the page
                const body = response.data
                if (
                  isPublicLink &&
                  response.status === 401 &&
                  body?.reason === 'INVALID_PASSWORD'
                ) {
                  window.location.reload()
                  /**
                   * A promise that never resolves or rejects, so that whoever invoked the fetch takes no further action...
                   * ...and the whole page just reloads because of the `window.location.reload()` above.
                   * This is a hack to avoid handling an explicit error at dozens of places where requests are made
                  */
                  return new Promise(() => { })
                }
                return response
              },
            },
          }}
        >
          <ElevationProvider>
          <Router
            basename={routingBaseName}
          >
            <Helmet
              titleTemplate="%s - ImageKit Dashboard"
              defaultTitle="ImageKit Dashboard"
              htmlAttributes={{ lang: i18n.language }}
            >
              <meta name="description" content="ImageKit Dashboard" />
            </Helmet>

            {
              !isPublicLink ?
                <SessionCheck /> :
                null
            }

            <RouterBackStackProvider>
              <Box height="100vh" overflowY="auto">
                <HideOnOAuth>
                {!isPublicLink && !isMediaLibraryWidget && window.user?.isSimulation ? (
                  <Box
                    width="full"
                    bgColor="#F55069"
                    textAlign="center"
                    lineHeight="40px"
                    color="white"
                    fontSize="16px"
                    fontWeight="bold"
                  >
                    <ChakraText>This is a simulation for another user</ChakraText>
                  </Box>
                ) : null}
                {(exceededArray && exceededArray.length > 0) &&
                  <Box
                    width="full"
                    padding="20px"
                    bgColor="#F55069"
                    color="white">
                    <ChakraText>
                      {
                        exceededArray.length > 1 ?
                          <>There seem to be some issues with your account:</> :
                          <>There seems to be an issue with your account:</>
                      }
                    </ChakraText>
                    <UnorderedList>
                      {exceededArray.map((el) => (
                        <ListItem>{el}</ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                }
                <DiscountedPricingClosableInfoBar />
                </HideOnOAuth>
                <Flex>
                  <HideOnOAuth>
                  {!isMediaLibraryWidget && !isPublicLink ? (
                    <Suspense fallback={<NavBarSkeleton />}>
                      <NavBarLayout />
                    </Suspense>
                  ) : null}
                  </HideOnOAuth>
                  <Suspense fallback={<FullPageLoadingSpinner />}>
                    <Switch>
                    {isMediaLibraryWidget || isPublicLink ? (
                      <>
                        {/* <Route path="/search">
                          <MediaLibraryRouter />
                        </Route>

                        <Route
                          exact
                          path="/media-library"
                          render={({ location }) => {
                            const hasSearch = new URLSearchParams(location.search).get('searchQuery')
                            return hasSearch
                              ? <Redirect to={{ pathname: '/search', search: location.search }} />
                              : <Redirect to="/media-library/" /> // or fall through to ML router
                          }}
                        /> */}
                        <Route exact path="/">
                          <Redirect to={routeSuffixToRedirectToForBaseUrl} />
                        </Route>

                        <Route path={['/media-library', '/media-collections', '/search']}>
                          <MediaLibraryRouter />
                        </Route>
                      </>
                    ) : (
                      <>
                        <Route exact path="/oauth">
                          <OAuthConsentPage />
                        </Route>

                        <Route exact path="/">
                          <HomePage />
                        </Route>

                        <Route path="/external-storage">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_INTEGRATION_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <ExternalStorageRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/path-policy">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_MEDIA_LIBRARY_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <CustomMetadataProvider>
                                  <PathPolicyRouter />
                                </CustomMetadataProvider>
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/creative-automation">
                          <PermissionGuard
                            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <CreativeAutomationRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/url-endpoints">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_INTEGRATION_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <UrlEndpointsRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/performance-alerts">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_PERFORMANCE_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <PerformanceRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/developer">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_DEVELOPERS_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <DeveloperPage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/settings">
                          {checkPermission({ user, permission: PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW }) ||
                            checkPermission({ user, permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE }) ? (
                            <CustomMetadataProvider>
                              <SettingsRouter />
                            </CustomMetadataProvider>
                          ) : (
                            <Redirect to="/" />
                          )}
                        </Route>

                        <Route path="/purge-cache">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_CLEAR_CACHE_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <PurgeCachePage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/profile">
                          <UserProfilePage />
                        </Route>

                        <Route path="/referrals">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_REFERRAL_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <ReferralsPage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/support">
                          <SupportRouter />
                        </Route>

                        <Route
                          path="/billing"
                          render={() =>
                            user?.pricingPlan.isPaidPlan ? (
                              <PermissionGuard
                                permission={PermissionsEnum.SELF_BILLING_VIEW}
                              >
                                {(hasPermission: boolean) =>
                                  hasPermission ? (
                                    user.isAWSMarketplaceSubscription ? (
                                      <AWSManagedPage />
                                    ) : <BillingRouter />
                                  ) : (
                                    <Redirect to="/" />
                                  )
                                }
                              </PermissionGuard>
                            ) : (
                              <Redirect to="/upgrade-plan" />
                            )
                          }
                        />

                        <Route path="/analytics">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_ANALYTICS_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <AnalyticsRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route path="/video-analytics">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_ANALYTICS_VIEW}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <VideoAnalyticsRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route
                          path="/user-management"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_TEAM_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <UserManagementRouter />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />

                        <Route
                          path="/invoices"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_BILLING_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <InvoiceRouter />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />
                        <Route
                          path="/audit-logs"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_TEAM_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <AuditLogsPage />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />

                        <Route
                          path="/dam-audit-logs"
                          render={() => (
                            <PermissionGuard
                              permission={PermissionsEnum.SELF_TEAM_VIEW}
                            >
                              {(hasPermission: boolean) =>
                                hasPermission ? (
                                  <MediaAuditLogsPage />
                                ) : (
                                  <Redirect to="/" />
                                )
                              }
                            </PermissionGuard>
                          )}
                        />

                        <Route exact path="/chat-review">
                          <ChatReviewListPage />
                        </Route>

                        <Route path="/chat-review/:sessionId">
                          <ChatReviewDetailPage />
                        </Route>

                        <Route
                          path="/upgrade-plan"
                          render={() =>
                            <Redirect to="/change-plan" />
                          }
                        />

                        <Route path="/change-plan">
                          <PermissionGuard permission={PermissionsEnum.SELF_BILLING_MANAGE}>
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <PlanChangePage />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>


                        <Route
                          path={['/media-library', '/search', '/media-collections']}
                          render={() => (
                            <PermissionGuard permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW}>
                              {(has) => has ? <MediaLibraryRouter /> : <Redirect to="/" />}
                            </PermissionGuard>
                          )}
                        />

                        <Route path="/manage-accounts">
                          <AgencyManageAccountsRouter />
                        </Route>

                        <Route path="/agency-analytics">
                          <AgencyAnalyticsPage />
                        </Route>

                        <Route
                          path="/get-started"
                          render={() =>
                            user?.onboardingComplete ? (
                              <Redirect to="/" />
                            ) : (
                              <IDRegion />
                            )
                          }
                        />

                        <Route path="/labs">
                          <PermissionGuard
                            permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
                          >
                            {(hasPermission: boolean) =>
                              hasPermission ? (
                                <LabsRouter />
                              ) : (
                                <Redirect to="/" />
                              )
                            }
                          </PermissionGuard>
                        </Route>

                        <Route exact path="/notifications">
                          <NotificationPage />
                        </Route>
                      </>
                    )}

                    <Route>
                      <NotFoundPage />
                    </Route>
                  </Switch>
                  </Suspense>
                </Flex>

                {/* AI Agent Components are now mounted in MediaLibraryRouter for proper MLContext access */}
              </Box>
            </RouterBackStackProvider>
            <GlobalStyle />
          </Router>
          </ElevationProvider>
        </FetchProvider>
      </LazyErrorBoundary> : <></>
  )
}

export default withLazySentryProfiler(IKDashboardApp)
